import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FileText, Download, RefreshCw, AlertTriangle, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, Search, X
} from 'lucide-react';
import { Toast } from '../../ui/Toast';
import ChampSelect from '../../ui/ChampSelect';
import EnTeteSection from '../EnTeteSection';
import ChargementSection from '../ChargementSection';
import { chargerSynthese, telechargerPDF } from './apiRapportsPointage';

/**
 * ============================================================================
 * RAPPORTS DE POINTAGE
 * ============================================================================
 *
 * Demandé par Daniel le 14/10/2026, deuxième semaine de la rentrée : ne plus
 * ouvrir la base jour par jour pour savoir qui a pointé, et tenir les réunions
 * avec les agents - « à telle date, telle heure, voici ton pointage ».
 *
 * ⚠️ L'ÉCRAN EST LA COPIE DU PDF (décision du 14/10/2026 au soir : « le PDF
 * est plus joli, mieux présenté et plus compréhensible que la page, fais
 * exactement la même chose »). Même ordre, mêmes couleurs, mêmes tableaux que
 * backend2/templates/rapportPointageTemplate.js : bandeau de titre, encadrés
 * de chiffres, constats numérotés, anomalies par type, rafales, badges
 * refusés, personnes qui n'ont jamais pointé, puis un bloc par personne.
 * Toute évolution de l'un se reporte sur l'autre.
 *
 * Trois différences seulement, propres à l'écran : les filtres, les boutons
 * PDF, et les blocs de personne FERMÉS PAR DÉFAUT (un clic sur le bandeau
 * bleu ouvre le relevé complet ; demande de Daniel, même soir).
 *
 * Tout le calcul est côté serveur (services/pointage/RapportPointageService.js).
 * Les dates restent des chaînes 'AAAA-MM-JJ' : jamais de `new Date('2026-10-14')`,
 * lu comme de l'UTC, qui afficherait la veille.
 */

const ACCENT = '#7c3aed';
const ACCENT_SOMBRE = '#5b21b6';

const POPULATIONS = [
  { valeur: 'tous', libelle: 'Tout le personnel' },
  { valeur: 'personnel', libelle: 'Personnel à horaire fixe' },
  { valeur: 'enseignants', libelle: 'Enseignants' }
];
const LIBELLE_POPULATION = Object.fromEntries(POPULATIONS.map((p) => [p.valeur, p.libelle]));

const JOURS_COURTS = { lundi: 'lun.', mardi: 'mar.', mercredi: 'mer.', jeudi: 'jeu.', vendredi: 'ven.', samedi: 'sam.', dimanche: 'dim.' };

const dateFr = (iso) => {
  const [a, m, j] = String(iso || '').split('-');
  return a && m && j ? `${j}/${m}/${a}` : '';
};

const dateCourte = (iso, jour) => {
  const [, m, j] = String(iso || '').split('-');
  return `${JOURS_COURTS[jour] || ''} ${j}/${m}`.trim();
};

const filtresServeur = (f, employeId = null) => ({
  du: f.du || null,
  au: f.au || null,
  population: f.population,
  employeId
});

const sansAccents = (texte) => String(texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const joursAttendus = (personne) => personne.lignes.filter((l) => !l.constats.some((x) => x.code === 'jour_non_travaille')).length;

// ----------------------------------------------------------------------------
// Les pièces du document, reprises du modèle PDF
// ----------------------------------------------------------------------------

// Les couleurs du PDF : bleu KLASYS pour les bandeaux, vert-bleu pour les
// en-têtes de tableau, rouge pour ce qui demande une explication.
const BLEU = '#246990';
const VERT_BLEU = '#319190';

const STYLES_ENCADRE = {
  primaire: 'border-[#246990] bg-[#246990] text-white',
  alerte: 'border-[#f59e0b] bg-[#fef3c7] text-gray-800',
  danger: 'border-[#ef4444] bg-[#fee2e2] text-gray-800',
  neutre: 'border-gray-300 bg-gray-50 text-gray-800'
};

const Encadre = ({ libelle, valeur, detail, style = 'neutre' }) => (
  <div className={`flex-1 rounded-md border-2 px-3 py-3 text-center ${STYLES_ENCADRE[style]}`}>
    <p className="text-[11px] font-bold uppercase tracking-wide">{libelle}</p>
    <p className="my-1 text-2xl font-bold">{valeur}</p>
    <p className="text-[11px] opacity-90">{detail}</p>
  </div>
);

// De l'air entre les sections : demande de Daniel du 14/10/2026, un écran
// chargé « fatigue les yeux ».
const TitreSection = ({ children }) => (
  <h2 className="mb-3 mt-10 border-b border-[#cfe0ea] pb-1.5 text-[15px] font-bold" style={{ color: BLEU }}>{children}</h2>
);

const SousTitreSection = ({ children }) => (
  <h3 className="mb-2.5 mt-8 text-[13px] font-bold" style={{ color: BLEU }}>{children}</h3>
);

/** Des noms en étiquettes séparées, plutôt qu'une phrase de quarante noms. */
const Etiquettes = ({ noms }) => (
  <div className="flex flex-wrap gap-1.5">
    {noms.map((nom) => (
      <span key={nom} className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700">{nom}</span>
    ))}
  </div>
);

/** Les constats : un sujet, un chiffre, une explication courte. */
const TableauConstats = ({ constats }) => (
  <Tableau entetes={[{ libelle: 'Sujet', largeur: '20%' }, { libelle: 'Chiffre', largeur: '12%' }, { libelle: 'Constat' }]}>
    {constats.map((c, index) => (
      <tr key={`${c.sujet}-${index}`} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
        <Cellule gauche><span className="font-semibold">{c.sujet}</span></Cellule>
        <Cellule><span className={`text-[15px] font-bold ${c.grave ? 'text-red-700' : 'text-gray-800'}`}>{c.chiffre}</span></Cellule>
        <Cellule gauche>{c.texte}</Cellule>
      </tr>
    ))}
  </Tableau>
);

const Tableau = ({ entetes, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr>
          {entetes.map(({ libelle, largeur, onTrier, sens }) => (
            <th
              key={libelle}
              className="border border-white px-3 py-2 text-center text-[11px] font-bold text-white"
              style={{ backgroundColor: VERT_BLEU, width: largeur }}
              aria-sort={sens ? (sens === 'croissant' ? 'ascending' : 'descending') : undefined}
            >
              {onTrier ? (
                <button
                  type="button"
                  onClick={onTrier}
                  className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 font-bold text-white hover:bg-white/15"
                  title="Trier"
                >
                  {libelle}
                  {sens === 'croissant' && <ChevronUp size={13} />}
                  {sens === 'decroissant' && <ChevronDown size={13} />}
                  {!sens && <ChevronsUpDown size={13} className="opacity-70" />}
                </button>
              ) : libelle}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Cellule = ({ children, gauche = false, attenuee = false, insecable = false }) => (
  <td className={`border border-gray-200 px-3 py-2.5 align-top leading-relaxed ${gauche ? 'text-left' : 'text-center'} ${attenuee ? 'text-gray-500' : 'text-gray-800'} ${insecable ? 'whitespace-nowrap' : ''}`}>
    {children}
  </td>
);

const Vide = () => <span className="text-gray-300">-</span>;

const Constats = ({ constats }) => {
  if (!constats.length) return <span className="text-emerald-700">Conforme</span>;
  return constats.map((c, index) => (
    <div key={`${c.code}-${index}`} className={c.grave ? 'font-bold text-red-700' : 'text-gray-500'}>{c.libelle}</div>
  ));
};

const classeLigne = (grave, index) => (grave ? 'bg-[#fff5f5]' : (index % 2 ? 'bg-gray-50' : 'bg-white'));

// Le journal des erreurs de pointage (31/10/2026) : tout passage à la borne
// qui n'a rien enregistré, expliqué en mots. Un matin où tous les badges
// échouaient, le rapport n'avait rien à montrer. Même section dans le PDF.
const CLASSE_GRAVITE = { erreur: 'font-bold text-red-700', refus: 'font-semibold text-amber-700', info: 'font-semibold text-gray-500' };
const quandCause = (c) => (c.premier.date === c.dernier.date
  ? `${dateFr(c.premier.date)}, ${c.premier.heure} - ${c.dernier.heure}`
  : `du ${dateFr(c.premier.date)} au ${dateFr(c.dernier.date)}`);

const JournalErreurs = ({ journal, ecole }) => {
  if (!journal?.disponible) {
    return <p className="text-xs text-gray-600">Les passages à la borne ne sont pas encore consignés dans cette école.</p>;
  }
  if (!journal.total) return <p className="text-xs text-gray-600">Aucun badge refusé ni aucune erreur à la borne sur la période.</p>;
  const france = ecole.afficherFrance;
  return (
    <>
      <p className="mb-3 text-xs text-gray-600">
        Chaque passage à la borne qui n&apos;a rien enregistré : {journal.total} sur la période
        {journal.erreurs > 0 && (
          <>
            , dont <span className="font-bold text-red-700">{journal.erreurs} erreur(s) du logiciel</span> (ce n&apos;est pas la faute des
            personnes ; ces pointages sont à saisir à la main)
          </>
        )}
        .
      </p>
      <Tableau
        entetes={[
          { libelle: "Ce qui s'est passé", largeur: '16%' },
          { libelle: 'Ce que ça veut dire', largeur: '26%' },
          { libelle: 'Que faire', largeur: '20%' },
          { libelle: 'Nombre', largeur: '7%' },
          { libelle: 'Qui', largeur: '17%' },
          { libelle: 'Quand' }
        ]}
      >
        {journal.causes.map((c, index) => (
          <tr key={c.cause} className={classeLigne(false, index)}>
            <Cellule gauche><span className={CLASSE_GRAVITE[c.gravite]}>{c.libelle}</span></Cellule>
            <Cellule gauche>{c.explication}</Cellule>
            <Cellule gauche>{c.conseil}</Cellule>
            <Cellule><span className={`text-[15px] ${CLASSE_GRAVITE[c.gravite]}`}>{c.nombre}</span></Cellule>
            <Cellule gauche><Etiquettes noms={c.personnes} /></Cellule>
            <Cellule gauche>{quandCause(c)}</Cellule>
          </tr>
        ))}
      </Tableau>
      <SousTitreSection>Passage par passage</SousTitreSection>
      <Tableau
        entetes={[
          { libelle: 'Date', largeur: '9%' },
          { libelle: france ? `Heure ${ecole.villeLocale}` : 'Heure', largeur: '8%' },
          ...(france ? [{ libelle: 'Heure France', largeur: '6%' }] : []),
          { libelle: 'Personne', largeur: '15%' },
          { libelle: 'Cours concerné', largeur: '20%' },
          { libelle: "Ce qui s'est passé", largeur: '16%' },
          { libelle: 'Réponse de la borne' }
        ]}
      >
        {journal.lignes.map((l, index) => (
          <tr key={`${l.date}-${l.heure?.locale}-${l.personne}-${index}`} className={classeLigne(l.gravite === 'erreur', index)}>
            <Cellule gauche insecable>{dateFr(l.date)}</Cellule>
            <Cellule>{l.heure?.locale || <Vide />}</Cellule>
            {france && <Cellule attenuee>{l.heure?.france || <Vide />}</Cellule>}
            <Cellule gauche>{l.personne}</Cellule>
            <Cellule gauche>{l.cours || <Vide />}</Cellule>
            <Cellule gauche><span className={CLASSE_GRAVITE[l.gravite]}>{l.libelle}</span></Cellule>
            <Cellule gauche>
              {l.reponse || <Vide />}
              {l.detailTechnique && <div className="mt-0.5 font-mono text-[10px] text-gray-500">Détail technique : {l.detailTechnique}</div>}
            </Cellule>
          </tr>
        ))}
      </Tableau>
    </>
  );
};

/** Le tableau jour par jour d'une personne, colonne pour colonne comme le PDF. */
const TableauJours = ({ personne, ecole }) => {
  const enseignant = personne.categorie === 'enseignant';
  const france = ecole.afficherFrance;
  const ville = ecole.villeLocale;
  const debut = enseignant ? 'Début' : 'Arrivée';
  const fin = enseignant ? 'Fin' : 'Départ';
  const entetes = [
    { libelle: 'Date', largeur: '8%' },
    ...(enseignant ? [{ libelle: 'Cours', largeur: '16%' }] : []),
    { libelle: 'Prévu', largeur: '9%' },
    { libelle: france ? `${debut} ${ville}` : debut, largeur: '7%' },
    ...(france ? [{ libelle: `${debut} France`, largeur: '7%' }] : []),
    { libelle: france ? `${fin} ${ville}` : fin, largeur: '7%' },
    ...(france ? [{ libelle: `${fin} France`, largeur: '7%' }] : []),
    ...(enseignant ? [] : [{ libelle: 'Pause', largeur: '8%' }]),
    { libelle: 'Durée', largeur: '7%' },
    { libelle: 'Constats' }
  ];

  return (
    <Tableau entetes={entetes}>
      {personne.lignes.map((l, index) => {
        const grave = l.constats.some((c) => c.grave);
        return (
          <tr key={`${l.date}-${l.prevuDebut || ''}-${index}`} className={classeLigne(grave, index)}>
            <Cellule gauche insecable>{dateCourte(l.date, l.jour)}</Cellule>
            {enseignant && <Cellule gauche>{l.cours || '-'}</Cellule>}
            <Cellule insecable>{l.prevuDebut ? `${l.prevuDebut} - ${l.prevuFin || ''}` : '-'}</Cellule>
            <Cellule>{l.arrivee?.locale || <Vide />}</Cellule>
            {france && <Cellule attenuee>{l.arrivee?.france || <Vide />}</Cellule>}
            <Cellule>{l.depart?.locale || <Vide />}</Cellule>
            {france && <Cellule attenuee>{l.depart?.france || <Vide />}</Cellule>}
            {!enseignant && <Cellule>{l.pause ? `${l.pause.sortie || '-'} - ${l.pause.retour || '-'}` : <Vide />}</Cellule>}
            <Cellule>{l.duree || <Vide />}</Cellule>
            <Cellule gauche><Constats constats={l.constats} /></Cellule>
          </tr>
        );
      })}
    </Tableau>
  );
};

/** Le bloc d'une personne : le bandeau bleu du PDF, qui s'ouvre au clic. */
const BlocPersonne = ({ personne, ecole, ouvert, basculer, genererPDF, pdfEnCours }) => {
  const Chevron = ouvert ? ChevronDown : ChevronRight;
  const c = personne.compteurs || {};
  const details = [
    personne.fonction,
    personne.groupe ? `Groupe ${personne.groupe}` : (personne.categorie === 'personnel' ? "Horaire de l'établissement" : null),
    personne.horaire ? `Horaire : ${personne.horaire}` : null,
    personne.aUnBadge ? null : 'Aucun badge attribué'
  ].filter(Boolean).join('  |  ');
  const resume = (personne.categorie === 'personnel'
    ? [
      `${personne.journeesPointees} jour(s) pointé(s) sur ${joursAttendus(personne)}`,
      c.retard ? `${c.retard} retard(s)` : null,
      c.depart_manquant ? `${c.depart_manquant} départ(s) non badgé(s)` : null,
      c.double_passage ? `${c.double_passage} double(s) passage(s)` : null,
      c.aucun_pointage ? `${c.aucun_pointage} jour(s) sans pointage` : null,
      c.erreur_borne ? `${c.erreur_borne} erreur(s) du logiciel` : null,
      c.badge_refuse ? `${c.badge_refuse} badge(s) refusé(s)` : null
    ]
    : [
      `${personne.coursPointes} cours pointé(s) sur ${personne.coursPrevus} prévu(s)`,
      c.retard ? `${c.retard} retard(s)` : null,
      c.fin_manquante ? `${c.fin_manquante} fin(s) non badgée(s)` : null,
      c.erreur_borne ? `${c.erreur_borne} erreur(s) du logiciel` : null,
      c.badge_refuse ? `${c.badge_refuse} badge(s) refusé(s)` : null
    ]).filter(Boolean).join('  |  ');

  return (
    <div className="mt-4">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={ouvert}
        onClick={basculer}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculer(); } }}
        className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-white ${ouvert ? 'rounded-t' : 'rounded'}`}
        style={{ backgroundColor: BLEU }}
      >
        <Chevron size={16} className="flex-shrink-0 opacity-80" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-bold">{personne.nomComplet}</p>
          <p className="truncate text-xs opacity-95">{details}</p>
        </div>
        {!ouvert && (
          <span className={`hidden flex-shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold sm:block ${personne.anomaliesGraves ? 'bg-white/90 text-red-700' : 'bg-white/20 text-white'}`}>
            {personne.jamaisPointe
              ? 'Jamais pointé'
              : (personne.anomaliesGraves ? `${personne.anomaliesGraves} anomalie(s)` : 'Conforme')}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); genererPDF(personne.id); }}
          disabled={Boolean(pdfEnCours)}
          className="flex h-7 flex-shrink-0 items-center gap-1.5 rounded border border-white/60 px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
          title={`Relevé PDF de ${personne.nomComplet}`}
        >
          {pdfEnCours === personne.id
            ? <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            : <Download size={12} />}
          Relevé PDF
        </button>
      </div>

      {ouvert && (
        <>
          <div className="bg-[#eef5f9] px-3 py-2 text-xs text-[#1a4f6e]">{resume}</div>
          {personne.constats?.length > 0 && (
            <div className="border-x border-gray-200 bg-white px-3 py-4">
              <TableauConstats constats={personne.constats} />
            </div>
          )}
          {personne.jamaisPointe && !c.badge_refuse
            ? (
              <div className="border border-t-0 border-gray-200 px-3 py-2 text-[13px] text-gray-700">
                Aucun pointage sur la période
                {personne.categorie === 'personnel'
                  ? ` (${c.aucun_pointage || 0} journée(s) où l'école a badgé)`
                  : ` (${personne.coursPrevus} cours prévus à l'emploi du temps)`}.
                {personne.aUnBadge ? '' : ' Aucun badge attribué : cette personne ne peut pas pointer.'}
              </div>
            )
            : <TableauJours personne={personne} ecole={ecole} />}
        </>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// L'onglet
// ----------------------------------------------------------------------------

const RapportsPointage = () => {
  const [chargement, setChargement] = useState(true);
  const [rechargement, setRechargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [donnees, setDonnees] = useState(null);
  const [filtres, setFiltres] = useState({ du: '', au: '', population: 'tous' });
  const [recherche, setRecherche] = useState('');
  const [triBadge, setTriBadge] = useState(null);
  const changerTriBadge = () => setTriBadge((avant) => (
    avant === null ? 'croissant' : (avant === 'croissant' ? 'decroissant' : null)
  ));
  const [ouverts, setOuverts] = useState(() => new Set());
  const [pdfEnCours, setPdfEnCours] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const premierChargement = useRef(true);

  const charger = useCallback(async (f) => {
    if (premierChargement.current) setChargement(true);
    else setRechargement(true);
    setErreur(null);
    try {
      const resultat = await chargerSynthese(filtresServeur(f));
      setDonnees(resultat);
      // La période par défaut est décidée par le serveur (début de l'année
      // scolaire, aujourd'hui à l'heure de l'école) : on la reprend dans les
      // champs pour qu'elle se voie et se modifie.
      setFiltres((avant) => ({ ...avant, du: resultat.periode.du, au: resultat.periode.au }));
    } catch (e) {
      // Un échec ne s'affiche jamais comme un rapport vide (norme §6).
      setErreur(e.message || 'Chargement impossible');
    } finally {
      premierChargement.current = false;
      setChargement(false);
      setRechargement(false);
    }
  }, []);

  useEffect(() => {
    charger(filtres);
    // Premier chargement seulement ; ensuite, « Actualiser ».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const genererPDF = async (employeId = null) => {
    setPdfEnCours(employeId || 'tous');
    try {
      await telechargerPDF(filtresServeur(filtres, employeId));
      setToast({ show: true, message: employeId ? 'Relevé de pointage généré.' : 'Rapport de pointage généré.', type: 'success' });
    } catch (e) {
      setToast({ show: true, message: `PDF impossible : ${e.message}`, type: 'error' });
    } finally {
      setPdfEnCours(null);
    }
  };

  const basculer = (id) => setOuverts((avant) => {
    const suivant = new Set(avant);
    if (suivant.has(id)) suivant.delete(id);
    else suivant.add(id);
    return suivant;
  });

  // L'ordre du PDF : ceux qui ont pointé d'abord, le personnel avant les
  // enseignants, puis par nom.
  const personnes = useMemo(() => {
    const terme = sansAccents(recherche.trim());
    return [...(donnees?.personnes || [])]
      .filter((p) => !terme || sansAccents(`${p.nomComplet} ${p.fonction || ''}`).includes(terme))
      .sort((a, b) => Number(a.jamaisPointe) - Number(b.jamaisPointe)
        || (a.categorie === b.categorie ? 0 : (a.categorie === 'personnel' ? -1 : 1))
        || a.nomComplet.localeCompare(b.nomComplet, 'fr'));
  }, [donnees, recherche]);

  if (chargement) {
    return <ChargementSection accent={ACCENT} libelle="Calcul du rapport de pointage…" />;
  }

  const s = donnees?.synthese;
  const ecole = donnees?.ecole || { villeLocale: "l'école", afficherFrance: false };
  const jamais = (donnees?.personnes || []).filter((p) => p.jamaisPointe);
  // Tri de la colonne Badge (demande de Daniel, 14/10/2026) : un clic met les
  // badges attribués en tête, un second les non attribués, un troisième rend
  // l'ordre alphabétique. Le nom départage toujours.
  const jamaisTries = [...jamais].sort((a, b) => {
    if (triBadge) {
      const ecart = Number(b.aUnBadge) - Number(a.aUnBadge);
      if (ecart) return triBadge === 'croissant' ? ecart : -ecart;
    }
    return a.nomComplet.localeCompare(b.nomComplet, 'fr');
  });
  const champDate = 'h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm font-normal text-gray-800 focus:border-[#7c3aed] focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/15';

  return (
    <div className="space-y-5">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((avant) => ({ ...avant, show: false }))}
      />

      <EnTeteSection
        icone={FileText}
        titre="Rapports de pointage"
        sousTitre="Qui a pointé, quand, et ce qui ne va pas - à l'heure de l'école et de France"
        accent={ACCENT}
        accentSombre={ACCENT_SOMBRE}
        actions={(
          <button
            type="button"
            onClick={() => genererPDF()}
            disabled={Boolean(pdfEnCours) || !donnees}
            className="flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
            style={pdfEnCours || !donnees ? undefined : { backgroundColor: ACCENT }}
          >
            {pdfEnCours === 'tous'
              ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              : <Download size={13} />}
            PDF de l&apos;école
          </button>
        )}
      />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200/80 bg-gray-50/60 p-4">
        <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
          Du
          <input
            type="date"
            value={filtres.du}
            max={filtres.au || undefined}
            onChange={(e) => setFiltres((avant) => ({ ...avant, du: e.target.value }))}
            className={champDate}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
          Au
          <input
            type="date"
            value={filtres.au}
            min={filtres.du || undefined}
            max={donnees?.periode?.aujourdhui || undefined}
            onChange={(e) => setFiltres((avant) => ({ ...avant, au: e.target.value }))}
            className={champDate}
          />
        </label>
        <div className="flex min-w-[220px] flex-col gap-1 text-xs font-semibold text-gray-600">
          Population
          <ChampSelect
            valeur={filtres.population}
            onChange={(valeur) => setFiltres((avant) => ({ ...avant, population: valeur }))}
            options={POPULATIONS}
            className="bg-white font-normal"
          />
        </div>
        <button
          type="button"
          onClick={() => charger(filtres)}
          disabled={rechargement}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {rechargement
            ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            : <RefreshCw size={13} />}
          Actualiser
        </button>
      </div>

      {erreur && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
            <AlertTriangle size={15} className="text-red-600" />
          </div>
          <p className="text-xs font-semibold text-red-700">Le rapport n&apos;a pas pu être calculé : {erreur}</p>
        </div>
      )}

      {s && (
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 text-[13px] leading-relaxed text-gray-800 md:p-8">
          {/* Le document, comme le PDF. */}
          <div className="rounded-md py-2 text-center text-[17px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: BLEU }}>
            Rapport de pointage
          </div>
          <p className="mb-4 mt-1.5 text-center text-sm text-gray-600">
            {[
              `Du ${dateFr(donnees.periode.du)} au ${dateFr(donnees.periode.au)}`,
              LIBELLE_POPULATION[donnees.filtres?.population] || null,
              ecole.afficherFrance ? `Heures de ${ecole.villeLocale} et de France` : null
            ].filter(Boolean).join(' - ')}
          </p>

          {!donnees.journal?.disponible && (
            <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Les badges refusés par la borne ne sont pas encore consignés dans cette école : le rapport ne montre que les pointages acceptés.
            </p>
          )}

          {/* Chaque encadré se lit seul : ce qu'on compte, sur quelle base.
              Les personnes en personnes, les badges en badges (Daniel,
              14/10/2026 : « 81 jours sur 272, un pointage ou les deux ? »).
              Même grille dans le PDF. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {s.personnel.effectif > 0 && (
              <>
                <Encadre libelle="Personnel qui a badgé" valeur={`${s.personnel.ontPointe} sur ${s.personnel.effectif}`} detail="personnes, au moins une fois sur la période" style="primaire" />
                <Encadre libelle="Arrivées badgées" valeur={`${s.personnel.arriveesBadgees} sur ${s.personnel.journeesAttendues}`} detail={`attendues : ${s.personnel.effectif} personnes × ${s.personnel.joursDeBadge} jours de badge`} style="primaire" />
                <Encadre libelle="Départs badgés" valeur={`${s.personnel.departsBadges} sur ${s.personnel.journeesAttendues}`} detail={`même base ; ${s.anomalies.find((x) => x.code === 'depart_manquant')?.nombre || 0} journée(s) passée(s) sans départ`} style={s.personnel.departsBadges < s.personnel.arriveesBadgees ? 'alerte' : 'primaire'} />
                <Encadre libelle="N'ont jamais badgé" valeur={`${s.personnel.jamaisPointe} sur ${s.personnel.effectif}`} detail={`personnes, dont ${s.personnel.jamaisPointeSansBadge} sans badge attribué`} style={s.personnel.jamaisPointe ? 'alerte' : 'neutre'} />
              </>
            )}
            {s.enseignants.effectif > 0 && (
              <Encadre libelle="Cours badgés" valeur={`${s.enseignants.coursPointes} sur ${s.enseignants.coursPrevus}`} detail={`cours à l'emploi du temps sur la période ; ${s.enseignants.avecCours} enseignant(s) sur ${s.enseignants.effectif} en ont`} style={s.enseignants.taux < 50 ? 'alerte' : 'primaire'} />
            )}
            <Encadre libelle="Anomalies à expliquer" valeur={s.anomalies.filter((a) => a.grave).reduce((n, a) => n + a.nombre, 0)} detail="retards, départs, jours sans pointage : voir le tableau" style="danger" />
          </div>

          <TitreSection>Constats</TitreSection>
          <TableauConstats constats={s.constats} />

          <TitreSection>Journal des erreurs de pointage</TitreSection>
          <JournalErreurs journal={s.journalErreurs} ecole={ecole} />

          {s.arriveesParJour?.length > 0 && (
            <>
              <SousTitreSection>Heure d&apos;arrivée réelle, jour par jour</SousTitreSection>
              <Tableau entetes={[{ libelle: 'Jour', largeur: '16%' }, { libelle: 'Horaire prévu', largeur: '16%' }, { libelle: 'Arrivée médiane', largeur: '16%' }, { libelle: 'Écart', largeur: '16%' }, { libelle: 'Arrivées comptées' }]}>
                {s.arriveesParJour.map((j, index) => (
                  <tr key={j.jour} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <Cellule gauche><span className="font-semibold capitalize">{j.jour}</span></Cellule>
                    <Cellule>{j.prevu}</Cellule>
                    <Cellule>{j.mediane}</Cellule>
                    <Cellule>
                      <span className={j.ecartMinutes > 15 ? 'font-bold text-red-700' : 'text-gray-700'}>
                        {j.ecartMinutes > 0 ? `+${j.ecartMinutes} min` : `${j.ecartMinutes} min`}
                      </span>
                    </Cellule>
                    <Cellule>{j.nombre}</Cellule>
                  </tr>
                ))}
              </Tableau>
              <p className="mt-2 text-xs text-gray-500">
                La médiane : la moitié des arrivées a lieu avant, l&apos;autre après. Un écart sur tout un jour de la semaine
                dit plus souvent un horaire inadapté qu&apos;un retard individuel.
              </p>
            </>
          )}

          <TitreSection>Anomalies par type</TitreSection>
          {s.anomalies.length ? (
            <Tableau entetes={[{ libelle: 'Anomalie', largeur: '24%' }, { libelle: 'Nombre', largeur: '8%' }, { libelle: 'Personnes concernées' }]}>
              {s.anomalies.map((a, index) => (
                <tr key={a.code} className={classeLigne(a.grave, index)}>
                  <Cellule gauche><span className={a.grave ? 'font-semibold' : ''}>{a.libelle}</span></Cellule>
                  <Cellule><span className={`font-bold ${a.grave ? 'text-red-700' : 'text-gray-600'}`}>{a.nombre}</span></Cellule>
                  <Cellule gauche><Etiquettes noms={a.personnes} /></Cellule>
                </tr>
              ))}
            </Tableau>
          ) : <p className="text-xs text-gray-600">Aucune anomalie sur la période.</p>}

          {s.rafales.length > 0 && (
            <>
              <SousTitreSection>Rafales de badges</SousTitreSection>
              <Tableau entetes={[{ libelle: 'Date', largeur: '12%' }, { libelle: 'Heures', largeur: '12%' }, { libelle: 'Personnes', largeur: '10%' }, { libelle: 'Sens', largeur: '10%' }, { libelle: 'Qui' }]}>
                {s.rafales.map((r, index) => (
                  <tr key={`${r.date}-${r.debut}-${r.sens}`} className={classeLigne(false, index)}>
                    <Cellule>{dateFr(r.date)}</Cellule>
                    <Cellule>{r.debut} - {r.fin}</Cellule>
                    <Cellule>{r.nombre} en {r.minutes} min</Cellule>
                    <Cellule>{r.sens}</Cellule>
                    <Cellule gauche><Etiquettes noms={r.personnes} /></Cellule>
                  </tr>
                ))}
              </Tableau>
            </>
          )}

          {jamais.length > 0 && (
            <>
              <SousTitreSection>N&apos;ont jamais pointé sur la période ({jamais.length})</SousTitreSection>
              <Tableau
                entetes={[
                  { libelle: 'Nom', largeur: '32%' },
                  { libelle: 'Fonction', largeur: '30%' },
                  { libelle: 'Catégorie', largeur: '18%' },
                  { libelle: 'Badge', onTrier: changerTriBadge, sens: triBadge }
                ]}
              >
                {jamaisTries.map((p, index) => (
                  <tr key={p.id} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <Cellule gauche><span className="font-semibold">{p.nomComplet}</span></Cellule>
                    <Cellule gauche>{p.fonction || '-'}</Cellule>
                    <Cellule gauche>{p.categorie === 'enseignant' ? 'Enseignant' : 'Personnel'}</Cellule>
                    <Cellule>
                      {p.aUnBadge
                        ? <span className="text-gray-700">Attribué</span>
                        : <span className="font-semibold text-red-700">Non attribué</span>}
                    </Cellule>
                  </tr>
                ))}
              </Tableau>
            </>
          )}

          <TitreSection>Détail par personne</TitreSection>
          <p className="text-xs text-gray-600">
            Heures relues dans le fuseau où elles ont été enregistrées, puis données à l&apos;heure de {ecole.villeLocale}
            {ecole.afficherFrance ? " et à l'heure de France" : ''}. Écarts recalculés contre l&apos;horaire prévu le jour du badge.
            Cliquer sur un nom ouvre son relevé.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher un nom ou une fonction…"
                aria-label="Rechercher une personne"
                className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-9 text-sm focus:border-[#7c3aed] focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/15"
              />
              {recherche && (
                <button
                  type="button"
                  onClick={() => setRecherche('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOuverts(ouverts.size ? new Set() : new Set(personnes.map((p) => p.id)))}
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50"
            >
              {ouverts.size ? 'Tout replier' : 'Tout déplier'}
            </button>
          </div>

          {personnes.length === 0 && (
            <p className="mt-3 text-sm text-gray-500">Personne ne correspond à cette recherche.</p>
          )}
          {personnes.map((p) => (
            <BlocPersonne
              key={p.id}
              personne={p}
              ecole={ecole}
              ouvert={ouverts.has(p.id)}
              basculer={() => basculer(p.id)}
              genererPDF={genererPDF}
              pdfEnCours={pdfEnCours}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RapportsPointage;
